/**
 * Integration GraphQL Queries
 *
 * 7 queries for external system connectivity:
 * - getIntegration (single)
 * - listIntegrations (paginated)
 * - getIntegrationsByType
 * - getIntegrationsByHealthStatus
 * - getIntegrationsWithOpenCircuitBreaker
 * - getLifeCriticalIntegrations
 * - getIntegrationUtilizationReports
 *
 * @see graphql_gateway/src/integration_api/integration_graphql.rs - Backend implementation
 */

import { gql } from '@apollo/client';
import {
  INTEGRATION_FIELDS,
  INTEGRATION_CONNECTION_FIELDS,
  INTEGRATION_UTILIZATION_REPORT_FIELDS,
} from './fragments';

/** Get integration by ID */
export const GET_INTEGRATION = gql`
  ${INTEGRATION_FIELDS}
  query GetIntegration($integrationId: String!) {
    getIntegration(integrationId: $integrationId) {
      ...IntegrationFields
    }
  }
`;

/** List integrations for tenant with pagination */
export const LIST_INTEGRATIONS = gql`
  ${INTEGRATION_CONNECTION_FIELDS}
  query ListIntegrations($tenantId: String!, $limit: Int, $cursor: String) {
    listIntegrations(tenantId: $tenantId, limit: $limit, cursor: $cursor) {
      ...IntegrationConnectionFields
    }
  }
`;

/** Get integrations by type (REST_API, GRAPHQL, WEBHOOK, etc.) */
export const GET_INTEGRATIONS_BY_TYPE = gql`
  ${INTEGRATION_FIELDS}
  query GetIntegrationsByType($tenantId: String!, $integrationType: IntegrationType!) {
    getIntegrationsByType(tenantId: $tenantId, integrationType: $integrationType) {
      ...IntegrationFields
    }
  }
`;

/** Get integrations by health status */
export const GET_INTEGRATIONS_BY_HEALTH_STATUS = gql`
  ${INTEGRATION_FIELDS}
  query GetIntegrationsByHealthStatus($tenantId: String!, $healthStatus: IntegrationHealthStatus!) {
    getIntegrationsByHealthStatus(tenantId: $tenantId, healthStatus: $healthStatus) {
      ...IntegrationFields
    }
  }
`;

/** Get integrations with open circuit breakers (critical for incident response) */
export const GET_INTEGRATIONS_WITH_OPEN_CIRCUIT_BREAKER = gql`
  ${INTEGRATION_FIELDS}
  query GetIntegrationsWithOpenCircuitBreaker($tenantId: String!) {
    getIntegrationsWithOpenCircuitBreaker(tenantId: $tenantId) {
      ...IntegrationFields
    }
  }
`;

/** Get life-critical integrations (require dual-control approval) */
export const GET_LIFE_CRITICAL_INTEGRATIONS = gql`
  ${INTEGRATION_FIELDS}
  query GetLifeCriticalIntegrations($tenantId: String!) {
    getLifeCriticalIntegrations(tenantId: $tenantId) {
      ...IntegrationFields
    }
  }
`;

/** Get integration utilization reports */
export const GET_INTEGRATION_UTILIZATION_REPORTS = gql`
  ${INTEGRATION_UTILIZATION_REPORT_FIELDS}
  query GetIntegrationUtilizationReports($tenantId: String!) {
    getIntegrationUtilizationReports(tenantId: $tenantId) {
      ...IntegrationUtilizationReportFields
    }
  }
`;
